import React, { Component } from 'react';
import { defineMessages, injectIntl } from 'react-intl';
import { Link } from 'react-router';
import Highlight from 'react-highlighter';
import moment from 'moment';

const messages = defineMessages({
  NO_USER_FOUND: {
    id: 'CompanyUserList.NO_USER_FOUND',
    defaultMessage: 'Es wurden keine Benutzer gefunden.',
  },
  OWNER: {
    id: 'CompanyUserList.OWNER',
    defaultMessage: 'Owner',
  },
  ADMIN: {
    id: 'CompanyUserList.ADMIN',
    defaultMessage: 'Admin',
  },
  DEACTIVATED: {
    id: 'CompanyUserList.DEACTIVATED',
    defaultMessage: 'Deaktiviert',
  },
  USER: {
    id: 'CompanyUserList.USER',
    defaultMessage: 'User',
  },
  EXPERT_USER: {
    id: 'CompanyUserList.EXPERT_USER',
    defaultMessage: 'Nutzer (Video-Lizenz)',
  },
  FREE_USER: {
    id: 'CompanyUserList.FREE_USER',
    defaultMessage: 'Kostenlos',
  },
  TABLE_NAME_AND_PHONE: {
    id: 'CompanyUserList.TABLE_NAME_AND_PHONE',
    defaultMessage: 'Name & Telefon',
  },
  TABLE_EMAIL: {
    id: 'CompanyUserList.TABLE_EMAIL',
    defaultMessage: 'E-Mail-Adresse',
  },
  TABLE_ROLES: {
    id: 'CompanyUserList.TABLE_ROLES',
    defaultMessage: 'Rollen',
  },
  TABLE_ACTIONS: {
    id: 'CompanyUserList.TABLE_ACTIONS',
    defaultMessage: 'Aktionen',
  },
  TOOLTIP_EDIT: {
    id: 'CompanyUserList.TOOLTIP_EDIT',
    defaultMessage: 'Benutzer anzeigen / bearbeiten',
  },
  TOOLTIP_CHANGE_ROLE: {
    id: 'CompanyUserList.TOOLTIP_CHANGE_ROLE',
    defaultMessage: 'Rollen ändern',
  }
});

class CompanyUserList extends Component {
  constructor(props) {
    super(props);
    const savedState = localStorage.getItem('CompanyUserListTableState');
    const defaultState = {
      sortColumn: 'name',
      sortDirection: 'asc',
      columnWidths: {
        col0: 300, // Name
        col1: 250, // Email
        col2: 200, // Roles
      }
    };
    
    this.state = savedState ? { ...defaultState, ...JSON.parse(savedState) } : defaultState;
    this.resizingColumn = null;
    this.startX = 0;
    this.startWidth = 0;
  }

  componentWillUnmount() {
    document.removeEventListener('mousemove', this.handleResizeMove);
    document.removeEventListener('mouseup', this.handleResizeEnd);
  }

  saveState = () => {
    const { sortColumn, sortDirection, columnWidths } = this.state;
    localStorage.setItem('CompanyUserListTableState', JSON.stringify({ sortColumn, sortDirection, columnWidths }));
  }

  toggleSort = (column) => {
    this.setState(prevState => {
      let nextDir = 'asc';
      if (prevState.sortColumn === column) {
        nextDir = prevState.sortDirection === 'asc' ? 'desc' : 'asc';
      }
      return {
        sortColumn: column,
        sortDirection: nextDir
      };
    }, this.saveState);
  }

  handleResizeStart = (e, columnKey) => {
    e.preventDefault();
    e.stopPropagation();
    this.resizingColumn = columnKey;
    this.startX = e.pageX;
    this.startWidth = this.state.columnWidths[columnKey];
    
    document.addEventListener('mousemove', this.handleResizeMove);
    document.addEventListener('mouseup', this.handleResizeEnd);
  };

  handleResizeMove = (e) => {
    if (!this.resizingColumn) return;
    const diff = e.pageX - this.startX;
    const newWidth = Math.max(50, this.startWidth + diff);
    this.setState(prevState => ({
      columnWidths: {
        ...prevState.columnWidths,
        [this.resizingColumn]: newWidth
      }
    }));
  };

  handleResizeEnd = () => {
    this.resizingColumn = null;
    document.removeEventListener('mousemove', this.handleResizeMove);
    document.removeEventListener('mouseup', this.handleResizeEnd);
    this.saveState();
  };

  renderSortIcon = (column) => {
    const { sortColumn, sortDirection } = this.state;
    if (sortColumn !== column) {
      return <i className="fa fa-sort" style={{ marginLeft: '8px', color: 'var(--text-muted)', opacity: 0.3 }} />;
    }
    return <i className={`fa ${sortDirection === 'asc' ? 'fa-sort-alpha-asc' : 'fa-sort-alpha-desc'}`} style={{ marginLeft: '8px', color: 'var(--text-color)' }} />;
  }

  render() {
    const { items, filter, company, isGlobalAdmin, isCompanyAdmin, intl: { formatMessage } } = this.props;
    
    let filteredItems = items || [];
    if (filter) {
      const lowerFilter = filter.toLowerCase();
      filteredItems = filteredItems.filter(companyUser => {
        const u = companyUser.user;
        return (u.name && u.name.toLowerCase().includes(lowerFilter)) ||
               (u.email && u.email.toLowerCase().includes(lowerFilter)) ||
               (u.phone && u.phone.toLowerCase().includes(lowerFilter));
      });
    }

    if (!(filteredItems && filteredItems.length)) {
      return <p style={{ color: '#94a3b8', textAlign: 'center', padding: '40px' }}>{formatMessage(messages.NO_USER_FOUND)}</p>;
    }

    const { sortColumn, sortDirection, columnWidths } = this.state;
    const sortedItems = [...filteredItems].sort((a, b) => {
      if (!sortColumn) return 0;
      
      let valA, valB;
      if (sortColumn === 'name') {
        valA = (a.user.name || '').toLowerCase();
        valB = (b.user.name || '').toLowerCase();
      } else if (sortColumn === 'email') {
        valA = (a.user.email || '').toLowerCase();
        valB = (b.user.email || '').toLowerCase();
      } else if (sortColumn === 'roles') {
        // Simple priority for roles sorting: Owner > Admin > User > Inactive
        const getPriority = (cu) => {
          if (cu.is_inactive) return 4;
          if (cu.is_owner) return 1;
          if (cu.is_admin) return 2;
          return 3;
        };
        valA = getPriority(a);
        valB = getPriority(b);
      }
      
      if (valA === valB) return 0;
      if (valA === undefined || valA === null) return sortDirection === 'asc' ? 1 : -1;
      if (valB === undefined || valB === null) return sortDirection === 'asc' ? -1 : 1;
      
      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      return sortDirection === 'asc' ? 1 : -1;
    });

    const thBaseStyle = {
      padding: '16px 24px',
      color: 'var(--text-muted)',
      fontWeight: 'bold',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      position: 'relative',
      cursor: 'pointer',
      userSelect: 'none'
    };

    const getThStyle = (key) => ({
      ...thBaseStyle,
      width: `${columnWidths[key]}px`,
      minWidth: `${columnWidths[key]}px`,
      maxWidth: `${columnWidths[key]}px`,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    });

    const resizerStyle = {
      position: 'absolute',
      right: 0,
      top: 0,
      bottom: 0,
      width: '6px',
      cursor: 'col-resize',
      backgroundColor: 'transparent',
      zIndex: 1,
    };

    const renderResizer = (key) => (
      <div 
        className="column-resizer"
        style={resizerStyle} 
        onMouseDown={(e) => this.handleResizeStart(e, key)}
        onClick={(e) => e.stopPropagation()}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--text-muted)'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
      />
    );

    return (
      <div style={{ width: '100%', overflowX: 'auto' }}>
        <table className="responsive-table" style={{ minWidth: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
          <thead>
            <tr style={{ backgroundColor: 'var(--panel-bg)', borderBottom: '1px solid var(--panel-border)' }}>
              <th onClick={() => this.toggleSort('name')} style={getThStyle('col0')}>
                {formatMessage(messages.TABLE_NAME_AND_PHONE)}
                {this.renderSortIcon('name')}
                {renderResizer('col0')}
              </th>
              <th onClick={() => this.toggleSort('email')} style={getThStyle('col1')}>
                {formatMessage(messages.TABLE_EMAIL)}
                {this.renderSortIcon('email')}
                {renderResizer('col1')}
              </th>
              <th onClick={() => this.toggleSort('roles')} style={getThStyle('col2')}>
                {formatMessage(messages.TABLE_ROLES)}
                {this.renderSortIcon('roles')}
                {renderResizer('col2')}
              </th>
              <th style={{ ...thBaseStyle, padding: '16px 24px', width: 'auto', textAlign: 'right', cursor: 'default' }}>
                {formatMessage(messages.TABLE_ACTIONS)}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedItems.map(companyUser => {
              return (
                <tr key={companyUser.id} style={{ borderBottom: '1px solid var(--panel-border)', transition: 'background-color 0.2s', ':hover': { backgroundColor: 'var(--btn-default-hover-bg)' } }}>
                  
                  {/* Name & Phone */}
                  <td style={{ padding: '16px 24px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Link 
                      to={`/company/${company.id}/user/${companyUser.user_id}/`} 
                      style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '12px', overflow: 'hidden', color: 'var(--text-color)' }}
                      onMouseEnter={(e) => e.currentTarget.style.color = 'var(--primary-color, #3b82f6)'}
                      onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-color)'}
                    >
                      <div style={{ width: '32px', height: '32px', minWidth: '32px', borderRadius: '6px', backgroundColor: 'rgba(59, 130, 246, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#3b82f6', fontSize: '14px' }}>
                        <i className="fa fa-user" />
                      </div>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <div style={{ fontWeight: 'bold', color: 'inherit', fontSize: '15px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {filter ? <Highlight search={filter}>{companyUser.user.name}</Highlight> : companyUser.user.name}
                        </div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '13px', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 'normal' }}>
                          {filter ? <Highlight search={filter}>{companyUser.user.phone || 'Keine Telefonnummer'}</Highlight> : (companyUser.user.phone || 'Keine Telefonnummer')}
                        </div>
                      </div>
                    </Link>
                  </td>
                  
                  {/* Email */}
                  <td data-label={formatMessage(messages.TABLE_EMAIL)} style={{ padding: '16px 24px', color: 'var(--text-color)', fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {filter ? <Highlight search={filter}>{companyUser.user.email}</Highlight> : companyUser.user.email}
                  </td>
                  
                  {/* Roles */}
                  <td data-label={formatMessage(messages.TABLE_ROLES)} style={{ padding: '16px 24px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'nowrap', overflow: 'hidden' }}>
                      {companyUser.is_owner && (
                        <span style={{ display: 'inline-block', padding: '4px 8px', borderRadius: '4px', backgroundColor: '#3b82f6', color: '#fff', fontSize: '11px', fontWeight: 'bold' }}>
                          {formatMessage(messages.OWNER).toUpperCase()}
                        </span>
                      )}
                      {companyUser.is_admin ? (
                        <span style={{ display: 'inline-block', padding: '4px 8px', borderRadius: '4px', backgroundColor: '#06b6d4', color: '#fff', fontSize: '11px', fontWeight: 'bold' }}>
                          {formatMessage(messages.ADMIN).toUpperCase()}
                        </span>
                      ) : null}

                      {companyUser.is_inactive && (
                        <span style={{ display: 'inline-block', padding: '4px 8px', borderRadius: '4px', backgroundColor: '#ef4444', color: '#fff', fontSize: '11px', fontWeight: 'bold' }}>
                          {formatMessage(messages.DEACTIVATED).toUpperCase()}
                        </span>
                      )}
                    </div>
                  </td>
                  
                  {/* Actions */}
                  <td style={{ padding: '16px 24px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', alignItems: 'center' }}>
                      <Link 
                        to={`/company/${company.id}/user/${companyUser.user_id}/`}
                        title={formatMessage(messages.TOOLTIP_EDIT)}
                        style={{ width: '32px', height: '32px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', backgroundColor: 'var(--btn-default-bg)', border: '1px solid var(--btn-default-border)', textDecoration: 'none' }}
                      >
                        <i className="fa fa-pencil" />
                      </Link>
                      <Link 
                        to={`/company/${company.id}/user/${companyUser.user_id}/delete/?from=list`}
                        title="Entfernen"
                        style={{ width: '32px', height: '32px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ef4444', backgroundColor: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', textDecoration: 'none' }}
                      >
                        <i className="fa fa-trash" />
                      </Link>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }
}

export default injectIntl(CompanyUserList);
